import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { FormArray } from '@angular/forms';
import { Observable } from 'rxjs';

import { CompanyVendorForm } from '../classes/form-groups/company-vendor-form';

@Injectable({
  providedIn: 'root'
})
export class CompanyVendorOfficeService {
  constructor(private http: HttpClient) {}

  /**
   * Replace the full set of client offices this vendor serves for one
   * client. An empty array clears the selection.
   *
   * Uses the v3 endpoint rather than the legacy v2 route, which rejected a body
   * with fewer than one office and so made "remove every office" impossible.
   */
  saveCompanyVendorOffices(
    CompanyId: number,
    VendorId: number,
    companyOfficeIds: number[]
  ): Observable<{ companyOfficeIds: number[] }> {
    return this.http.post<{ companyOfficeIds: number[] }>(
      `/api/v3/vendors/${VendorId}/company/${CompanyId}/company-vendor-offices`,
      { companyOfficeIds }
    );
  }

  /**
   * Build the save calls for every client whose office selection the admin
   * actually changed.
   *
   * Untouched clients are deliberately skipped. The endpoint replaces the whole
   * set, so posting a client the admin never opened would wipe its offices —
   * including when that client's selections failed to load into the form.
   * Adding and deselecting both mark the array dirty, so dirty is the accurate
   * signal for "this one changed".
   */
  saveDirtySelections(
    CompanyVendorsFormArray: FormArray<CompanyVendorForm>,
    VendorId: number
  ): Observable<{ companyOfficeIds: number[] }>[] {
    return CompanyVendorsFormArray.controls
      .filter((companyVendorForm) => {
        const CompanyVendorOfficesFormArray =
          companyVendorForm.controls['CompanyVendorOffices'];

        return (
          !!CompanyVendorOfficesFormArray && CompanyVendorOfficesFormArray.dirty
        );
      })
      .map((companyVendorForm) => {
        const { CompanyId } = companyVendorForm.getRawValue();
        const selections = companyVendorForm.controls['CompanyVendorOffices']!
          .value as { CompanyOfficeId?: number }[];

        const companyOfficeIds = selections
          .map((companyVendorOffice) => companyVendorOffice.CompanyOfficeId)
          .filter(
            (companyOfficeId): companyOfficeId is number =>
              typeof companyOfficeId === 'number'
          );

        return this.saveCompanyVendorOffices(
          Number(CompanyId),
          VendorId,
          companyOfficeIds
        );
      });
  }
}
